import { Fragment, isValidElement } from 'react';
import { clamp } from 'lodash-es';

import { css, styled } from '@mui/material';

import DataTableSkeleton from '../DataTableSkeleton/DataTableSkeleton';
import KeyValuePairsSkeleton from '../KeyValuePairsSkeleton/KeyValuePairsSkeleton';
import PageHeaderSkeleton from '../PageHeaderSkeleton/PageHeaderSkeleton';
import Skeleton from '../Skeleton/Skeleton';
import { skeletonClasses } from '../Skeleton/theme';
import { detailPageSkeletonClasses } from './theme';

import type { DetailPageSkeletonOwnerState, DetailPageSkeletonProps } from './types';

export default function DetailPageSkeleton(props: DetailPageSkeletonProps) {
  const {
    anchorTabsCount = 3,
    pageHeaderProps,
    sections = [
      <KeyValuePairsSkeleton key={0} rows={8} />,
      <DataTableSkeleton key={1} showSideBorder tableProps={{ pageSize: 5 }} />,
    ],
    showAnchorTabs = true,
    ...others
  } = props;

  const ownerState: DetailPageSkeletonOwnerState = others;

  const anchorTabs = Array(clamp(anchorTabsCount, 1, 5)).fill(1);

  return (
    <DetailPageSkeletonRoot {...others} ownerState={ownerState}>
      <DetailPageSkeletonHeader
        showExtraActions
        breadcrumbsCount={3}
        {...pageHeaderProps}
        ownerState={ownerState}
      />
      {showAnchorTabs && (
        <DetailPageSkeletonAnchorTabs ownerState={ownerState}>
          {anchorTabs.map((_, i) => (
            <Skeleton key={i} variant="text" />
          ))}
        </DetailPageSkeletonAnchorTabs>
      )}
      <DetailPageSkeletonContent ownerState={ownerState}>
        {sections.map((section, sectionIndex) => {
          const key =
            !isValidElement(section) || section.key === undefined
              ? `detail-page-skeleton-section-${sectionIndex}`
              : section.key;
          return (
            <Fragment key={key}>
              <DetailPageSkeletonContentSection ownerState={ownerState}>
                <DetailPageSkeletonContentSectionHeader ownerState={ownerState}>
                  <Skeleton variant="text" />
                </DetailPageSkeletonContentSectionHeader>
                {section}
              </DetailPageSkeletonContentSection>
            </Fragment>
          );
        })}
      </DetailPageSkeletonContent>
    </DetailPageSkeletonRoot>
  );
}

const DetailPageSkeletonRoot = styled('div', {
  target: detailPageSkeletonClasses.root,
})<{ ownerState: DetailPageSkeletonOwnerState }>(
  () => css`
    width: 100%;
  `,
);

const DetailPageSkeletonHeader = styled(PageHeaderSkeleton, {
  target: detailPageSkeletonClasses.header,
})<{ ownerState: DetailPageSkeletonOwnerState }>(
  () => css`
    margin: 16px 32px;
  `,
);

const DetailPageSkeletonAnchorTabs = styled('div', {
  target: detailPageSkeletonClasses.anchorTabs,
})<{ ownerState: DetailPageSkeletonOwnerState }>(
  ({ theme }) => css`
    margin: 4px 32px 16px 32px;
    height: 44px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: ${theme.mixins.border('border/default')};

    .${skeletonClasses.root} {
      width: 60px;
      height: 27px;
    }
  `,
);

const DetailPageSkeletonContent = styled('div', {
  target: detailPageSkeletonClasses.content,
})<{ ownerState: DetailPageSkeletonOwnerState }>(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 48px;
    margin-top: 48px;
    padding: 0px 32px;
  `,
);

const DetailPageSkeletonContentSection = styled('div', {
  target: detailPageSkeletonClasses.contentSection,
})<{ ownerState: DetailPageSkeletonOwnerState }>(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
);

const DetailPageSkeletonContentSectionHeader = styled('div', {
  target: detailPageSkeletonClasses.contentSectionHeader,
})<{ ownerState: DetailPageSkeletonOwnerState }>(
  () => css`
    height: 27px;
    display: flex;
    align-items: center;

    .${skeletonClasses.root} {
      width: 80px;
      height: 18px;
    }
  `,
);
